import { SuiteHeader } from '@iamexperiences/suite-header';
import { IHeaderActionProps, ILocalizationMap, IOrganization, IUserData } from '@iamexperiences/suite-header';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { NavBarData } from '@suiteux/suiteux-shell-applauncher';
import i18next from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import * as React from 'react';
import { settingsManager } from '../shared/settings';
import { getMenuItems, getSelectedApp } from './enabledApps.util';
import { IHeaderProps } from './Header.types';
import { useAuth, AuthPrompt } from '@iamexperiences/feature-auth';
import { getIAMUXGroupsUrl, AppKeys, getIAMUXAppsUrl } from '@microsoft/portal-app/lib/environment';
import { defaultTelemetry, euTelemetry, updateTelemetryReference } from '../Telemetry/Telemetry';
import { IAppInfo } from '../Application/Main/interfaces';
import { Link } from '@fluentui/react';
import { useHeaderOrganizations } from './useHeaderOrganizations';
import { useCurrentOrganization } from '../Account/Organizations/NativeIdentity/useCurrentOrganization';
import { useSetTelemetry, useTelemetry } from '@iamexperiences/ecos-telemetry';
import { isUserInitiatedIPFEnabledInSetting } from '../InProductFeedback';
import { useAppContext } from '../Application/useAppContext';
import { useGetRegionalAndLanguageSettingsQuery } from '../Language/hooks';
import { getI18n } from 'react-i18next';
import { IConfiguration } from '@iamexperiences/suite-header';
import { useTenantBranding } from '../hooks/useTenantBranding';
import { useGetUserPhoto } from '../hooks/useGetUserPhoto';
import { useHistory } from 'react-router-dom';
import { MY_GROUPS_PATH } from '../shared/constants';
import { ITenantProps } from '@iamexperiences/suite-header/lib-commonjs/SuiteHeader/TenantList/ITenantProps';

const DEFAULT_HELPLINK = 'https://docs.microsoft.com/en-us/azure/active-directory/user-help/my-account-portal-overview';
const helpLink = settingsManager.get('panelHelpLink') || DEFAULT_HELPLINK;
const evoBase = settingsManager.get('azureInstance') || 'https://login.microsoftonline.com';
const myAppsUrl = settingsManager.get('myAppsUrl');
const myGroupsUrl = settingsManager.get('myGroupsUrl');
const myAccessUrl = settingsManager.get('myAccessUrl');
const viewAccountLink = settingsManager.get('meControlViewAccountLink') || '';
const isUniversalMeControlEnabled = settingsManager.get('isUniversalMeControlEnabled') || false;

const isRegionalEndpointEnabled = settingsManager.get('regionalCollectorEndpointEnabled') || false;

export const Header: React.FC<IHeaderProps> = (props) => {
  const { navBarData, hasError, getNavBarData, manageOrganizationsLinkOnClick } = props;

  const { auth } = useAuth();
  const { onShowIPF } = useAppContext().InProductFeedbackOption;
  const history = useHistory();

  const clientId = auth.clientId;
  const user = auth.user;
  const tenantId = user?.tenantId;
  const homeTenantId = user?.homeTenantId;
  const objectId = user?.objectId;
  const username = user?.username;
  const rawClaims = user?.rawClaims;
  const name = user?.name;
  const isGuest = tenantId !== homeTenantId;
  const isOfficeAppLauncherDisabled = isGuest || settingsManager.get('isOfficeAppLauncherDisabled');
  const claims = user && (user.rawClaims as { xms_pl?: unknown } | undefined);
  const [language, setLanguage] = useState(
    claims && typeof claims.xms_pl === 'string' ? claims.xms_pl : i18next.languages[0],
  );
  const getToken = useCallback(
    async (scope: string) => {
      return await auth.acquireToken({ scopes: [scope] });
    },
    [auth],
  );

  const configuration: IConfiguration = React.useMemo(
    () => ({
      msGraphBaseUrl: settingsManager.getRequired('msGraphResourceName'),
      getToken: getToken,
    }),
    [getToken],
  );

  const userData: IUserData = {
    tenantId: tenantId || '',
    upn: username || '',
    name: name || '',
    homeTenantId: homeTenantId || '',
    rawClaims: rawClaims || {},
  };

  const [t] = useLocalization(['Account']);
  const { organizations, isLoading, error: headerOrganizationsError } = useHeaderOrganizations(configuration, userData);
  const { currentOrganization, showMyAccess, inEUTenant } = useCurrentOrganization();
  const { data: tenantBrandingUrl } = useTenantBranding(userData.tenantId);
  const { data: userPhotoUrl } = useGetUserPhoto();
  const setTelemetry = useSetTelemetry();
  const telemetry = useTelemetry();
  const localizationMap: ILocalizationMap = {
    apps: t('Apps'),
    tenantBrandingImageAlt: t('tenantBranding', { displayName: currentOrganization.orgName }),
    organizations: t('organizations'),
    loadingOrganizations: t('loadingOrganizations'),
    organizationsEmpty: t('emptyOrganizations'),
    finishLoading: t('loaded'),
    organizationError: t('getOrganizationsError'),
    switchOrgIntroText: t('switchOrgIntroText'),
    currentOrganizationTitle: t('currentOrganizationTitle'),
    otherOrganizationsTitle: t('otherOrganizationsTitle'),
    manageOrganizations: t('manageOrganizationsLinkText'),
    closePanelAriaLabel: t('Close'),
    helpPanelHeaderText: t('help'),
    accessibilityLink: t('accessibilityLinkText'),
    viewApps: t('viewApps'),
    helpPanelTermsOfUseLink: t('termsOfUse'),
    helpPanelPrivacyLink: t('privacy'),
    accountManager: t('accountManager'),
    switchOrganization: t('switchOrganization'),
    moreOptions: t('more'),
    leaveNewExp: t('leaveNewExp'),
    waffleNotAvailableHeader: t('fallbackWaffleHeaderText'),
    waffleNotAvailableDescription: t('fallbackWaffleMainText'),
  };

  const appsMap: Map<AppKeys, IAppInfo> = new Map<AppKeys, IAppInfo>();
  appsMap.set(AppKeys.account, {
    appKey: 'My Account',
    appName: t('My Account'),
    defaultUrl: window.location.origin,
  });

  myAppsUrl &&
    appsMap.set(AppKeys.apps, {
      appKey: 'My Apps',
      appName: t('myApps'),
      iamuxUrl: getIAMUXAppsUrl(),
      defaultUrl: myAppsUrl,
    });

  myGroupsUrl &&
    appsMap.set(AppKeys.groups, {
      appKey: 'My Groups',
      appName: t('myGroups'),
      iamuxUrl: getIAMUXGroupsUrl(),
      defaultUrl: myGroupsUrl,
    });

  myAccessUrl &&
    appsMap.set(AppKeys.access, {
      appKey: 'My Access',
      appName: t('myAccess'),
      defaultUrl: myAccessUrl,
    });

  const { defaultDisplayLanguage } = useGetRegionalAndLanguageSettingsQuery();

  useEffect(() => {
    // Default display language is set per user in the settings page, if it is not set, we use the top language detected by i18 language detector.
    if (defaultDisplayLanguage) {
      setLanguage(defaultDisplayLanguage);
      getI18n().changeLanguage(defaultDisplayLanguage);
    }
  }, [defaultDisplayLanguage]);

  useEffect(() => {
    const getHeaderData = async () => {
      const audience = settingsManager.get('officeSuiteHeaderAudience');
      if (!audience) {
        telemetry.error('officeSuieHeaderAudience setting not present', {});
      } else {
        const token = await auth.acquireToken({ scopes: [audience] });
        getNavBarData(token);
      }
    };
    getHeaderData();
  }, [tenantId, clientId, objectId, auth, telemetry]);

  useEffect(() => {
    if (inEUTenant) {
      if (isRegionalEndpointEnabled) {
        setTelemetry(euTelemetry);
        updateTelemetryReference(euTelemetry);
      }
    } else {
      setTelemetry(defaultTelemetry);
      updateTelemetryReference(defaultTelemetry);
    }
  }, [inEUTenant, setTelemetry]);

  const changeTenant = useCallback(
    (targetTenant: ITenantProps, currentTenant?: IOrganization) => {
      telemetry.reportCustomEvent('suite-header/switch-organization', {
        source: currentTenant?.displayName,
        target: targetTenant?.displayName,
      });

      auth.login({ tenantId: targetTenant.tenantId, loginHint: auth.user?.username });
    },
    [auth, telemetry],
  );

  const switchTo = useCallback(
    (upn: string) => {
      auth.login({
        loginHint: upn,
      });
    },
    [auth],
  );

  const switchAccount = useCallback(() => {
    auth.login({
      prompt: AuthPrompt.SELECT_ACCOUNT,
    });
  }, [auth]);

  const logOut = useCallback(() => {
    telemetry.reportCustomEvent('suite-header/MeControl/logOut', {
      message: 'The user logged out from MeControl',
    });
    auth.logout({ authority: auth.getAuthority(auth.user?.tenantId) });
  }, [auth, telemetry]);

  const handleHeaderRender = useCallback(() => {
    telemetry.reportCustomEvent('suite-header/rendered');
  }, [telemetry]);

  const handleHeaderButtonClick = useCallback(
    (buttonId: string) => {
      telemetry.reportCustomEvent('suite-header/button-clicked', { buttonId });
    },
    [telemetry],
  );

  const manageOrganizationControl = useCallback(() => {
    return (
      <Link href="#" onClick={manageOrganizationsLinkOnClick}>
        {t('manageOrganizationsLinkText')}
      </Link>
    );
  }, [t, manageOrganizationsLinkOnClick]);

  const handleAppBrandingClick = useCallback(() => {
    const homeLink = getSelectedApp() === AppKeys.groups ? MY_GROUPS_PATH : '/';
    if (homeLink !== window.location.pathname) {
      history.push(homeLink);
    } else {
      const region = document.getElementById('aria-live-region');
      if (region) {
        region.textContent =
          getSelectedApp() === AppKeys.groups ? t('homeLinkGroupsAnnouncement') : t('homeLinkAccountAnnouncement');
      }
    }
  }, [history, t]);

  const extraActions: IHeaderActionProps[] = React.useMemo(() => {
    const isInProductFeedbackEnabled = isUserInitiatedIPFEnabledInSetting(tenantId);
    if (isInProductFeedbackEnabled) {
      return [{ title: t('inProductFeedback'), iconName: 'Feedback', onClick: () => onShowIPF() }];
    }
    return [];
  }, [onShowIPF, t, tenantId]);

  if (language === undefined) {
    return null;
  }

  const appName = getSelectedApp() === AppKeys.groups ? t('myGroups') : t('My Account');

  return (
    <SuiteHeader
      appId={clientId}
      userData={{
        tenantDisplayName: currentOrganization.orgName,
        tenantId: tenantId || '',
        upn: username || '',
        name: name || '',
        imageUrl: userPhotoUrl,
        homeTenantId: homeTenantId || '',
        rawClaims: rawClaims ?? {},
      }}
      workloadId={'AADMyAccount'}
      navbarData={navBarData as NavBarData}
      culture={language}
      localizationMap={localizationMap}
      imageUrl={tenantBrandingUrl}
      appName={<h1>{appName}</h1>}
      appNameLabel={appName}
      onClickAppBranding={handleAppBrandingClick}
      enabledApps={getMenuItems(appsMap, showMyAccess, username, tenantId)}
      organizations={{
        value: organizations,
        isLoading,
      }}
      changeTenant={changeTenant}
      onSwitchTo={switchTo}
      onSwitch={switchAccount}
      onHeaderButtonClicked={handleHeaderButtonClick}
      onRenderComplete={handleHeaderRender}
      manageTenantControl={manageOrganizationControl}
      isMsalJsEnabled={true}
      hasError={hasError || headerOrganizationsError !== null}
      helpLink={helpLink}
      isOfficeAppLauncherDisabled={isOfficeAppLauncherDisabled}
      isUniversalMeControlEnabled={isUniversalMeControlEnabled}
      evoBase={evoBase}
      viewAccountLink={viewAccountLink}
      onSignOut={logOut}
      extraActions={extraActions}
    />
  );
};
